"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Lock } from "lucide-react";
import { useNotification } from "@/hooks/use-notification-v2";
import Link from "next/link";
import { Logo } from "@/components/brand/logo";

const loginSchema = z.object({
  email: z.email("E-mail inválido"),
  password: z.string().min(1, "Senha é obrigatória"),
  rememberMe: z.boolean().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const router = useRouter();
  const { showToast } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const result = await signIn("credentials", {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result?.error) {
        showToast(
          "E-mail ou senha incorretos. Verifique suas credenciais e tente novamente.",
          "error"
        );
        return;
      }

      if (result?.ok) {
        router.push("/dashboard");
        router.refresh();
      }
    } catch (_error) {
      showToast("Ocorreu um erro ao fazer login. Tente novamente.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-full items-center justify-center overflow-hidden bg-gradient-to-br from-[#0000fe] via-[#0000fe]/95 to-[#3afcf3]/90">
      {/* Decorative Background Elements */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-[radial-gradient(circle,#3afcf3,transparent_70%)] opacity-40 blur-3xl" />
        <div className="absolute right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-[radial-gradient(circle,#acfcf3,transparent_70%)] opacity-30 blur-3xl" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md px-6">
        {/* Card */}
        <div className="overflow-hidden rounded-2xl border border-white/20 bg-white shadow-[0_40px_100px_-50px_rgba(0,0,0,0.7)] backdrop-blur-xl">
          <div className="px-10 py-10">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <Logo width={160} height={53} />
            </div>

            <div className="mb-8 text-center">
              <h1 className="mb-2 text-2xl font-semibold text-[#000000]">
                Bem-vindo de volta
              </h1>
              <p className="text-sm text-[#464646]">
                Acesse sua conta para gerenciar propostas e comissões
              </p>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full space-y-5"
            >
              <div className="w-full space-y-2">
                <Label
                  htmlFor="email"
                  className="pl-11 text-xs font-semibold tracking-wider text-[#464646] uppercase"
                >
                  Email
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-[#464646]/60" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    autoComplete="email"
                    className="h-12 w-full rounded-2xl border-[#e5e5e5] bg-white !pr-4 !pl-12 text-sm text-[#000000] placeholder:text-[#464646]/50 focus:border-[#0000fe] focus:ring-2 focus:ring-[#0000fe]/20"
                    {...register("email")}
                  />
                </div>
                {errors.email && (
                  <p className="text-error text-xs">{errors.email.message}</p>
                )}
              </div>

              <div className="w-full space-y-2">
                <Label
                  htmlFor="password"
                  className="pl-11 text-xs font-semibold tracking-wider text-[#464646] uppercase"
                >
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-[#464646]/60" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="h-12 w-full rounded-2xl border-[#e5e5e5] bg-white !pr-4 !pl-12 text-sm text-[#000000] placeholder:text-[#464646]/50 focus:border-[#0000fe] focus:ring-2 focus:ring-[#0000fe]/20"
                    {...register("password")}
                  />
                </div>
                {errors.password && (
                  <p className="text-error text-xs">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 text-sm">
                <div className="inline-flex items-center gap-2 text-[#464646]">
                  <Checkbox id="rememberMe" {...register("rememberMe")} />
                  <label htmlFor="rememberMe" className="cursor-pointer">
                    Lembrar de mim
                  </label>
                </div>
                <Link
                  href="/forgot-password"
                  className="font-medium text-[#0000fe] hover:opacity-80"
                >
                  Esqueceu a senha?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="mt-6 h-12 w-full rounded-2xl bg-gradient-to-r from-[#0000fe] via-[#0000fe] to-[#3afcf3] text-base font-semibold text-white shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                    Entrando...
                  </span>
                ) : (
                  "Entrar"
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-white/70">
          © 2026 Topa+. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
